import * as dateFns from "date-fns";
import { useEffect, useRef, useState } from "react";
import z from "zod";
import apiService from "../../services/service";
import API_ROUTES from "../../api-routes";
import useDateTime from "../../hooks/useDateTime";
import { convertDataToCalendarEvents } from "./utils";
import { useSelector } from "react-redux";

const useMyCalendar = () => {
  const { formatDateTime } = useDateTime();
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventDetail, setEventDetail] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [milestonesLoading, setMilestonesLoading] = useState(false);
  const [showEventList, setShowEventList] = useState(false);
  const [sidebarBackBtn, setSidebarbackBtn] = useState(false);
  const [chipData, setChipData] = useState([
    { label: "Milestones", value: "Milestones", groupKey: "Event Type" },
  ]);
  const [eventItems, setEventItems] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [allEntities, setAllEntities] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const taskSelected = useRef(false);
  const [filterOptions, setFilterOptions] = useState({
    Entity: [],
    "Event Type": ["Milestones", "Tasks"],
    Owner: [],
    "Service Line": [],
  });
  const didMountChipRef = useRef(false);
  const allowedFilterValues = ["Milestones", "Tasks"];
  const { access, role: userRole } = useSelector((state) => state.login);
  const generateTreeSelectSelectedKeys = (
    groupedOptions,
    selectedItems = [],
  ) => {
    const result = {};

    Object.entries(groupedOptions).forEach(([group, children]) => {
      const selected = children.filter((child) =>
        selectedItems.includes(child),
      );

      if (selected.length > 0) {
        result[group] = {
          checked: false,
          partialChecked: selected.length < children.length,
        };

        selected.forEach((child) => {
          result[child] = {
            checked: true,
            partialChecked: false,
          };
        });
      }
    });

    return result;
  };
  const [selectedKeys, setSelectedKeys] = useState(
    generateTreeSelectSelectedKeys(filterOptions, ["Milestones"]),
  );

  const [eventsList, setEventsList] = useState([]);

  const handleSelectEvent = (event, backBtn) => {
    setIsOpen(true);
    setEventDetail(event);
    setShowEventList(false);
    if (backBtn) setSidebarbackBtn(true);
    else setSidebarbackBtn(false);
  };

  const handleViewMoreEvent = () => {
    setIsOpen(true);
    setShowEventList(true);
  };

  const fetchEntities = async () => {
    const { data } = await apiService(API_ROUTES.ALL_ENTITIES, "GET", {
      replace: {
        page: 1,
        pageSize: 100,
        searchValue: "",
      },
      schema: z.object({
        page: z.number().optional(),
        pageSize: z.number().optional(),
        search: z.string().optional(),
      }),
    });
    setAllEntities(data);
    if (data.length < 2) {
      setFilterOptions((prev) => {
        const { Entity, ...rest } = prev;
        return rest;
      });
    } else {
      setFilterOptions((prev) => ({
        ...prev,
        Entity: data,
      }));
    }
  };

  const fetchServices = async () => {
    const { data } = await apiService(API_ROUTES.GET_SERVICES, "GET");

    const uniqueServices = [
      ...new Set(access.flatMap((item) => item.services)),
    ];
    const accessServices =
      userRole !== "GXM_ADMIN"
        ? data.filter((service) => uniqueServices.includes(service.id))
        : data;

    setAllServices(accessServices);
    if (accessServices.length < 2) {
      setFilterOptions((prev) => {
        const { "Service Line": _, ...rest } = prev;
        return rest;
      });
    } else {
      setFilterOptions((prev) => ({
        ...prev,
        "Service Line": accessServices,
      }));
    }
  };

  const groupValues = (groupKey, filters) =>
    filters
      .filter((f) => f.groupKey === groupKey)
      .map((f) => f.value)
      .flat();

  const fetchEvents = async ({ year, month, day = "", filters = chipData }) => {
    setEventsLoading(true);

    const eventTypeFilters = groupValues("Event Type", filters)
      .map((f) => f.trim())
      .join(",");
    const entityFilters = groupValues("Entity", filters);
    const serviceFilters = groupValues("Service Line", filters);
    const ownerFilters = groupValues("Owner", filters);
    const { data } = await apiService(API_ROUTES.FETCH_EVENTS, "GET", {
      replace: {
        id: "",
        year,
        month,
        day,
        ...(eventTypeFilters
          ? { eventType: eventTypeFilters }
          : { eventType: "Milestones,Tasks" }),
        entity: entityFilters,
        service: serviceFilters,
        owner: ownerFilters,
      },
      schema: z.object({
        id: z
          .union([z.string().uuid(), z.string()])
          .transform((val) => {
            try {
              if (
                val === undefined ||
                val === null ||
                (typeof val === "string" && val === "")
              )
                return null;
              return val;
            } catch {
              throw new Error("Entity ID must be valid");
            }
          })
          .optional(),
        year: z.coerce
          .string()
          .regex(/^\d{4}$/, "Year must be a valid 4-digit number"),
        month: z
          .number()
          .int()
          .min(1, "Month must be between 1 and 12")
          .max(12, "Month must be between 1 and 12"),
        day: z
          .union([
            z
              .number()
              .int()
              .min(1, "Day must be between 1 and 31")
              .max(31, "Day must be between 1 and 31"),
            z.string(),
          ])
          .transform((val) => {
            try {
              if (
                val === undefined ||
                val === null ||
                (typeof val === "string" && val === "")
              )
                return null;
              const num = Number(val);
              return isNaN(num) ? null : num;
            } catch {
              throw new Error("Day must be valid");
            }
          })
          .optional(),
        eventType: z
          .string()
          .refine(
            (str) => {
              const val = str.split(",").map((s) => s.trim());
              return val.every((item) => allowedFilterValues.includes(item));
            },
            {
              message: `Filter must only contain: ${allowedFilterValues.join(", ")}`,
            },
          )
          .optional(),
        entity: z
          .union([
            z.string().transform((str) => str.split(",").map((s) => s.trim())),
            z.array(z.string()),
          ])
          .optional(),
        service: z
          .union([
            z.string().transform((str) => str.split(",").map((s) => s.trim())),
            z.array(z.string()),
          ])
          .optional(),
        owner: z
          .union([
            z.string().transform((str) => str.split(",").map((s) => s.trim())),
            z.array(z.string()),
          ])
          .optional(),
      }),
    });

    const result = convertDataToCalendarEvents(data);
    if (day !== "") {
      await setSelectedEvents(result);
      setEventsLoading(false);
      return;
    }
    setEvents(result);
    setEventsLoading(false);
  };

  const handleCloseEventDetailCalander = () => {
    setIsOpen(false);
    setEventDetail({});
  };

  useEffect(() => {
    fetchEntities();
    fetchServices();
    fetchEvents({ year, month });
  }, []);

  const onDateChange = (date) => {
    fetchEvents({
      year: dateFns.getYear(date),
      month: dateFns.getMonth(date) + 1,
    });
    setMonth(dateFns.getMonth(date) + 1);
    setYear(dateFns.getYear(date));
  };

  const fetchMilestonesSiblings = async (id) => {
    setMilestonesLoading(true);
    const data = await apiService(API_ROUTES.FETCH_MILESTONES_SIBLINGS, "GET", {
      replace: { id },
    });
    setMilestonesLoading(false);
    return data;
  };
  function formatDate(dateString) {
    const date = new Date(dateString);

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const month = monthNames[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();

    function getDaySuffix(day) {
      if (day > 3 && day < 21) return "th";
      switch (day % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    }

    const suffix = getDaySuffix(day);

    return `${month} ${day}${suffix}, ${year}`;
  }

  useEffect(() => {
    setEventsList([...events]);
  }, [events]);

  const handleChipData = (selectedValues) => {
    const chipArray = [];

    for (const [groupKey, items] of Object.entries(filterOptions)) {
      for (const item of items) {
        const key = typeof item === "object" ? item.id : item;
        const label = typeof item === "object" ? item.name : item;

        if (selectedValues[key]?.checked) {
          chipArray.push({ label, value: key, groupKey });
        }
      }
    }
    setChipData(chipArray);
  };

  useEffect(() => {
    if (didMountChipRef.current) {
      fetchEvents({
        year: year,
        month: month,
        filters: chipData,
      });
    } else {
      didMountChipRef.current = true;
    }
  }, [chipData]);

  const handleRemoveFilter = (chipToRemove) => {
    let filterChipData = [...chipData];
    const updatedKeys = { ...selectedKeys };

    if (chipToRemove.value === "Tasks") {
      taskSelected.current = false;
      filterChipData = filterChipData.filter((data) => {
        if (data?.groupKey === "Owner") {
          delete updatedKeys[data.value];
        }
        return data?.groupKey !== "Owner";
      });
    }
    const updatedChips = filterChipData.filter(
      (chip) =>
        !(
          chip.value === chipToRemove.value &&
          chip.groupKey === chipToRemove.groupKey
        ),
    );
    setChipData(updatedChips);

    delete updatedKeys[chipToRemove.value];
    setSelectedKeys(updatedKeys);
  };

  useEffect(() => {
    const isTaskSelected = Object?.keys(selectedKeys)?.includes("Tasks");
    if (["GXM_ADMIN", "GXM_MANAGER", "GXM_USER"].includes(userRole)) {
      setFilterOptions((prev) => ({
        ...prev,
        Owner: [
          { id: "GXM", name: "GXM", disable: !isTaskSelected },
          { id: "LSP", name: "LSP", disable: !isTaskSelected },
          { id: "Customer", name: "Customer", disable: !isTaskSelected },
        ],
      }));
    } else if (["LSP_USER", "LSP_MANAGER"].includes(userRole)) {
      setFilterOptions((prev) => ({
        ...prev,
        Owner: [
          { id: "LSP", name: "LSP", disable: !isTaskSelected },
          { id: "Customer", name: "Customer", disable: !isTaskSelected },
        ],
      }));
    } else {
      const filters = { ...filterOptions };
      delete filters["Owner"];
      setFilterOptions(filters);
    }
  }, [userRole, selectedKeys]);

  useEffect(() => {
    const treeNodes = { ...selectedKeys };
    const isTaskSelected = Object?.keys(selectedKeys)?.includes("Tasks");
    if (isTaskSelected && !taskSelected?.current) {
      taskSelected.current = true;
      const roleGroups = {
        GXM: ["GXM_ADMIN", "GXM_MANAGER", "GXM_USER"],
        LSP: ["LSP_USER", "LSP_MANAGER"],
      };

      if (roleGroups.GXM.includes(userRole)) {
        ["GXM", "LSP", "Customer"].forEach(
          (key) => (treeNodes[key] = { checked: true, partialChecked: false }),
        );
      } else if (roleGroups.LSP.includes(userRole)) {
        treeNodes.LSP = { checked: true, partialChecked: false };
      }
      setSelectedKeys(treeNodes);
    }
  }, [selectedKeys, taskSelected]);

  const handleSelectNode = (value) => {
    const isTaskSelected = Object?.keys(value)?.includes("Tasks");
    let treeNodes = { ...value };
    if (!isTaskSelected) {
      taskSelected.current = false;
      for (const element in value) {
        if (["Owner", "GXM", "LSP", "Customer"].includes(element)) {
          delete treeNodes[element];
        }
      }
    }
    setSelectedKeys(treeNodes);
  };

  return {
    year,
    events,
    items: eventsList,
    onDateChange,
    calendarLoading: eventsLoading,
    isOpen,
    setIsOpen,
    formatDate,
    eventDetail,
    setEventDetail,
    handleSelectEvent,
    handleCloseEventDetailCalander,
    fetchMilestonesSiblings,
    milestonesLoading,
    handleViewMoreEvent,
    showEventList,
    sidebarBackBtn,
    selectedKeys,
    setSelectedKeys,
    filterOptions,
    chipData,
    setChipData,
    handleRemoveFilter,
    handleChipData,
    eventItems,
    setEventItems,
    fetchEvents,
    selectedEvents,
    setSelectedEvents,
    handleSelectNode,
    taskSelected,
  };
};

export default useMyCalendar;
